/**
 * TypeScript types for the belief inspector (SPEC §2.6 item 7).
 *
 * Beliefs are dated, decaying hypotheses. The API returns BOTH the stored
 * confidence (as of the last test) and the decayed confidence-at-read —
 * anti-calcification is a property of the read, so the two diverge as a
 * belief ages untested.
 */

import type { AgentDomain } from './agentSettings';

export type BeliefStatus = 'active' | 'stale' | 'under_test' | 'retired';

export type BeliefScope = 'tenant_private' | 'industry' | 'platform';

export type BeliefGoalScope = 'goal_agnostic' | 'goal_specific';

export interface BeliefEvidence {
  id: string;
  test_description: string;
  result: string | null;
  sample_size: number | null;
  observed_at: string;
  confidence_after: number;
  /**
   * 'human_pin' / 'human_correction' rows are human assertions. They are
   * real evidence for THIS tenant but deliberately never count toward
   * cross-tenant (industry) promotion — an anti-Sybil guard.
   */
  source: string | null;
  is_human: boolean;
}

export interface Belief {
  id: string;
  statement: string;
  domain: AgentDomain;
  status: BeliefStatus;
  scope: BeliefScope;
  goal_scope: BeliefGoalScope;
  stored_confidence: number;
  decayed_confidence: number;
  decay_half_life_days: number;
  last_tested_at: string | null;
  age_days: number | null;
  /** Always within the 5-month floor — pinned beliefs included. */
  retest_by: string | null;
  collective_eligible: boolean;
  pattern_key: string | null;
  evidence_count: number;
  created_at: string | null;
}

export interface BeliefProposalRef {
  id: string;
  action_type: string;
  status: string;
  tier: string;
  created_at: string | null;
}

export interface BeliefDetail extends Belief {
  evidence: BeliefEvidence[];
  /** Recent proposals designed to test this belief (newest first). */
  proposals: BeliefProposalRef[];
}

export interface BeliefsListResponse {
  beliefs: Belief[];
  total: number;
  has_more: boolean;
}

export interface BeliefActionResponse {
  belief: BeliefDetail;
  /**
   * True when a statement rewrite cleared the belief's pattern_key: it no
   * longer participates in cross-tenant promotion. Deliberate §1.8 guard —
   * a repurposed belief cannot vote under a key it may no longer express.
   */
  detached_from_collective: boolean;
}

export function getBeliefStatusLabel(status: BeliefStatus): string {
  const labels: Record<BeliefStatus, string> = {
    active: 'Active',
    stale: 'Stale',
    under_test: 'Under Test',
    retired: 'Retired',
  };
  return labels[status] || status;
}

export function getBeliefStatusTone(
  status: BeliefStatus
): 'success' | 'warning' | 'info' | undefined {
  const tones: Record<BeliefStatus, 'success' | 'warning' | 'info' | undefined> = {
    active: 'success',
    stale: 'warning',
    under_test: 'info',
    retired: undefined,
  };
  return tones[status];
}

export function formatConfidence(value: number): string {
  return `${Math.round(value * 100)}%`;
}
